import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject, PLATFORM_ID, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class PropertyService {
  private favIdsSubject = new BehaviorSubject<Set<string>>(new Set());
  favIds$ = this.favIdsSubject.asObservable();

  private favListSubject = new BehaviorSubject<any[]>([]);
  favList$ = this.favListSubject.asObservable();

  data = signal<any>(null);
  // Shared snapshot of the existing backend status; no client-side counting.
  viewStatus = signal<any>(null);

  
  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  getAllProperties(page?:number,limit?:number): Observable<any> {
    return this.http
      .get(`${environment.api_URL}/property?page=${page??1}&limit=${limit??10}`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }
  getPropertieByID(id:string): Observable<any> {
    return this.http
      .get(`${environment.api_URL}/property/${id}`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }
  getCiRecord(id: string): Observable<any> {
    return this.http.get(`${environment.api_URL}/ci/${encodeURIComponent(id)}`);
  }

  getCiDashboard(): Observable<any> {
    return this.http.get(`${environment.api_URL}/ci/dashboard`, {
      params: { limit: '50' },
    });
  }

  getAllCluster(): Observable<any> {
    return this.http
      .get(`${environment.api_URL}/clusters`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }
  searchProperty(data: any): Observable<any> {
    return this.http.post(`${environment.api_URL}/property/search`, data).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        if (!this.isBrowser()) {
          return of({ status: false, message: 'SSR mode' });
        }
        return throwError(() => ({
          status: false,
          message: this.getErrorMessage(error),
          error,
        }));
      })
    );
  }
  getUserAllFavProp(): Observable<any> {
    return this.http
      .get(`${environment.api_URL}/fav`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!this.isBrowser()) {
            return of({ status: false, message: 'SSR mode' });
          }
          return throwError(() => ({
            status: false,
            message: this.getErrorMessage(error),
            error,
          }));
        })
      );
  }
  loadFavIds(): void {
    this.http.get<any>(`${environment.api_URL}/fav`).subscribe({
      next: (res) => {
        const data = res.data ?? [];
        const ids = new Set<string>(data.map((p: any) => p._id));
        this.favIdsSubject.next(ids);
        this.favListSubject.next(data);
      },
      error: () => {
        this.favIdsSubject.next(new Set());
        this.favListSubject.next([]);
      }
    });
  }

  getFavs(): Observable<any> {
    return this.http.get<any>(`${environment.api_URL}/fav`).pipe(catchError(this.handleError));
  }

  addFav(propertyId: string): Observable<any> {
    return this.http.post<any>(`${environment.api_URL}/fav/add`, { propertyId }).pipe(
      tap((res) => {
        const ids = new Set(this.favIdsSubject.value);
        ids.add(propertyId);
        this.favIdsSubject.next(ids);
        if (res?.status) {
          this.favListSubject.next(res?.status);
        }
      }),
      catchError(this.handleError)
    );
  }
  
  removeFav(propertyId: string): Observable<any> {
    return this.http.delete<any>(`${environment.api_URL}/fav/remove`, { body: { propertyId } }).pipe(
      tap(() => {
        // update ids
        const ids = new Set(this.favIdsSubject.value);
        ids.delete(propertyId);
        this.favIdsSubject.next(ids);
        const updated = this.favListSubject.value.filter(p => p._id !== propertyId);
        this.favListSubject.next(updated);
      }),
      catchError(this.handleError)
    );
  }

  isFav(propertyId: string): boolean {
    return this.favIdsSubject.value.has(propertyId);
  }

  propertyReq(propertyID: any): Observable<any> {
    return this.http
      .post(`${environment.api_URL}/property/request`, {propId:propertyID})
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!isPlatformBrowser(this.platformId)) {
            return of({ status: false, message: 'SSR mode' });
          }
          let apiMessage = 'An unexpected error occurred';

          if (error.error) {
            if (error.error.error?.message) {
              apiMessage = error.error.error.message;
            } else if (error.error.message) {
              apiMessage = error.error.message;
            } else if (typeof error.error === 'string') {
              apiMessage = error.error;
            }
          } else {
            apiMessage = error.message || 'Server error';
          }
          return throwError(() => ({
            status: false,
            message: apiMessage,
            originalError: error,
          }));
        })
      );
  }
  getViewedProp(propertyID: any): Observable<any> {
    return this.http
      .get(`${environment.api_URL}/property/viewed/${propertyID}`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!isPlatformBrowser(this.platformId)) {
            return of({ status: false, message: 'SSR mode' });
          }
          let apiMessage = 'An unexpected error occurred';

          if (error.error) {
            if (error.error.error?.message) {
              apiMessage = error.error.error.message;
            } else if (error.error.message) {
              apiMessage = error.error.message;
            } else if (typeof error.error === 'string') {
              apiMessage = error.error;
            }
          } else {
            apiMessage = error.message || 'Server error';
          }
          return throwError(() => ({
            status: false,
            message: apiMessage,
            originalError: error,
          }));
        })
      );
  }
  getViewedProperies(): Observable<any> {
    return this.http
      .get(`${environment.api_URL}/property/viewed-properties`)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          if (!isPlatformBrowser(this.platformId)) {
            return of({ status: false, message: 'SSR mode' });
          }
          let apiMessage = 'An unexpected error occurred';

          if (error.error) {
            if (error.error.error?.message) {
              apiMessage = error.error.error.message;
            } else if (error.error.message) {
              apiMessage = error.error.message;
            } else if (typeof error.error === 'string') {
              apiMessage = error.error;
            }
          } else {
            apiMessage = error.message || 'Server error';
          }
          return throwError(() => ({
            status: false,
            message: apiMessage,
            originalError: error,
          }));
        })
      );
  }
  getViewPropertyStatus(): Observable<any> {
    return this.http
      .get(`${environment.api_URL}/property/viewproperty`)
      .pipe(
        retry(1),
        tap(status => this.viewStatus.set(status)),
        catchError((error: HttpErrorResponse) => {
          if (!isPlatformBrowser(this.platformId)) {
            return of({ status: false, message: 'SSR mode' });
          }
          let apiMessage = 'An unexpected error occurred';

          if (error.error) {
            if (error.error.error?.message) {
              apiMessage = error.error.error.message;
            } else if (error.error.message) {
              apiMessage = error.error.message;
            } else if (typeof error.error === 'string') {
              apiMessage = error.error;
            }
          } else {
            apiMessage = error.message || 'Server error';
          }
          return throwError(() => ({
            status: false,
            message: apiMessage,
            originalError: error,
          }));
        })
      );
  }

  getPublicProperty(token: any): Observable<any> {
    return this.http.get(`${environment.api_URL}/property/view/${token}`).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        if (!this.isBrowser()) {
          return of({ status: false, message: 'SSR mode' });
        }
        return throwError(() => ({
          status: false,
          message: this.getErrorMessage(error),
          error,
        }));
      })
    );
  }

  createUserPreference(reqData: any): Observable<any> {
    return this.http
      .post(`${environment.api_URL}/cust-pref`, reqData)
      .pipe(
        retry(1),
        catchError((error: HttpErrorResponse) => {
          // Handle SSR
          if (!isPlatformBrowser(this.platformId)) {
            return of({ status: false, message: 'SSR mode' });
          }
          let apiMessage = 'An unexpected error occurred';
          if (error.error) {
            if (error.error.error?.message) {
              apiMessage = error.error.error.message;
            } else if (error.error.message) {
              apiMessage = error.error.message;
            } else if (typeof error.error === 'string') {
              apiMessage = error.error;
            }
          } else {
            apiMessage = error.message || 'Server error';
          }
          return throwError(() => ({
            status: false,
            message: apiMessage,
            originalError: error,
          }));
        })
      );
  }
  parsePriceRange(rangeStr: string) {
    const parts = rangeStr.replace(/[₹\s]/g, '').split(/[–-]/);
    const convertToNumber = (val: string): number => {
      const num = parseFloat(val);
      if (val.toLowerCase().includes('cr')) return num * 10000000;
      if (val.toLowerCase().includes('l')) return num * 100000;
      return num;
    };
  
    return {
      min: convertToNumber(parts[0]),
      max: convertToNumber(parts[1]),
      currency: '₹'
    };
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      return `Network error: ${error.error.message}`;
    } 
    if (error.error && error.error.message) {
      return Array.isArray(error.error.message) 
        ? error.error.message[0]  // Handle class-validator arrays
        : error.error.message;
    }
  
    return `Server Error: ${error.statusText || 'Internal Server Error'}`;
  }
  private handleError(error: HttpErrorResponse) {
    let message = 'Something went wrong';
    if (error.status === 403) message = error.error?.message ?? 'Upgrade to Premium for unlimited favourites';
    else if (error.status === 400) message = error.error?.message ?? 'Bad request';
    else if (error.status === 401) message = 'Please login to add favourites';
    else if (error.status === 0)   message = 'Network error. Check your connection';
    return throwError(() => ({ status: error.status, message }));
  }
  // data sharing
  setData(value: any) {
    this.data.set(value);
  }

  updateData(updater: (current: any) => any) {
    this.data.update(updater);
  }

}
